import React, { memo } from "react";
import type { ReactNode } from "react";
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { LargeContentBox } from "../LargeContentBox";

export interface PageHubCardItem {
  title: string;
  description: string;
  icon: ReactNode;
  onClick: () => void;
  enabled?: boolean;
}

export interface PageHubSection {
  title?: string;
  cards: PageHubCardItem[];
}

export interface PageHubProps {
  header: ReactNode;
  sections: PageHubSection[];
}

/**
 * PageHub
 *
 * Componente padrão para páginas "hub" do legado:
 * - layout centralizado
 * - grid responsivo
 * - cards com visual consistente e sofisticado (sem width fixa)
 */
const PageHub = memo<PageHubProps>(({ header, sections }) => {
  return (
    <LargeContentBox>
      <Stack
        sx={{
          /**
           * `LargeContentBox` usa `Paper` com `display: grid` + `justifyItems: "center"`.
           * Para evitar que o conteúdo fique “encolhido” no centro, forçamos o PageHub
           * a se esticar e ocupar a largura disponível do Paper.
           */
          width: "100%",
          justifySelf: "stretch",
          alignItems: "stretch",
          gap: 2,
        }}
      >
        {header}

        {sections.map((section, sectionIndex) => {
          const visibleCards = section.cards.filter(
            (card) => card.enabled !== false,
          );

          if (visibleCards.length === 0) {
            return null;
          }

          const mdColumns = (() => {
            /**
             * Ajuste de layout para deixar a grade mais “bem distribuída” e centralizada
             * dependendo da quantidade de cards visíveis.
             *
             * - 1 card  → 6 colunas (centralizado)
             * - 2 cards → 6 colunas (2 por linha, centralizado)
             * - 4 cards → 6 colunas (2 por linha, evita "3 + 1")
             * - 3+      → 4 colunas (3 por linha)
             */
            if (visibleCards.length <= 2) {
              return 6;
            }
            if (visibleCards.length === 4) {
              return 6;
            }
            return 4;
          })();

          return (
            <Stack key={`hub-section-${sectionIndex}`} spacing={2}>
              {section.title && (
                <Typography
                  variant="h6"
                  component="h2"
                  color="neutral800.main"
                  fontWeight={700}
                >
                  {section.title}
                </Typography>
              )}

              <Grid
                container
                spacing={{ xs: 1.5, md: 2 }}
                justifyContent="center"
                sx={{ width: "100%" }}
              >
                {visibleCards.map((card, cardIndex) => (
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={mdColumns}
                    key={`hub-card-${cardIndex}`}
                    sx={{
                      display: "flex",
                      /**
                       * Importante: como o grid pode ficar com colunas largas (ex.: md=6),
                       * centralizar o card aqui cria muito "vazio" ao redor.
                       * Deixamos o card preencher a coluna, e controlamos respiro via `spacing`.
                       */
                      justifyContent: "stretch",
                    }}
                  >
                    <Card
                      elevation={5}
                      sx={{
                        height: "100%",
                        borderRadius: "8px",
                        /**
                         * Evita que um card "sobrando" em uma linha (ex.: 4º card em md)
                         * pareça maior que os demais em breakpoints menores.
                         *
                         * - `width: 100%` mantém o card preenchendo o grid item
                         * - `maxWidth` limita o card a um tamanho consistente e agradável
                         */
                        width: "100%",
                        /**
                         * Para evitar muito espaço "vazio" dentro da coluna do Grid,
                         * o card deve ocupar a largura disponível do item.
                         */
                        maxWidth: "100%",
                      }}
                    >
                      <CardActionArea
                        onClick={card.onClick}
                        sx={{
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "stretch",
                          "&:hover": {
                            backgroundColor: "primary100.main",
                          },
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            paddingTop: "24px",
                          }}
                        >
                          <Box sx={{ marginBottom: "-8px" }}>{card.icon}</Box>
                        </Box>

                        <CardContent sx={{ flexGrow: 1 }}>
                          <Typography
                            gutterBottom
                            variant="h5"
                            component="div"
                            align="center"
                          >
                            {card.title}
                          </Typography>
                          <Typography
                            textAlign="center"
                            variant="body1"
                            color="neutral700.main"
                            fontWeight={500}
                            mb={2}
                          >
                            {card.description}
                          </Typography>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Stack>
          );
        })}
      </Stack>
    </LargeContentBox>
  );
});

PageHub.displayName = "PageHub";

export default PageHub;
